import React, { useContext, useState } from "react";
import cx from "classnames";
import { Icon, docs } from "Icons/index";
import { priceFormat } from "Utils/strings";
import t from "Utils/translation";
import Button from "Components/button/Button";
import TranslatedString from "Components/TranslatedString";
import useLocalStorage from "Utils/useLocalStorage";
import docsLinks from "@/docs.js";
import { AuthContext } from "@/App";
import QboxmailApi from "@/qboxmailApi";
import Modal from "Components/smallModal/smallModal";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import DailyMessagesPriceTable from "Components/DailyMessagesPriceTable";
import DeliverySpotBanner from "Components/DeliverySpotBanner";

const api = new QboxmailApi({ useCookie: true });

export const serviceType = {
  TRIAL_ENDUSER: "trial-enduser",
  TRIAL_RESELLER: "trial-reseller",
  PAID_ENDUSER: "paid-enduser",
  PAID_RESELLER: "paid-reseller",
};

const DocsLink = ({ docsKey }) => {
  const { getLoggedUser } = useContext(AuthContext);
  const lang = getLoggedUser().language;
  const onLinkClick = () => window.open(docsLinks[lang][docsKey]);

  return (
    <div className="flex items-center gap-1 text-sm cursor-pointer text-qb-blue" onClick={onLinkClick}>
      <Icon className="icon-stroke-accent" svg={docs} width="12px" />
      <p>{t("button.docs")}</p>
    </div>
  );
};

const PriceVat = () => (
  <div className="mt-16 text-xs text-qb-text-a60">
    <TranslatedString>infoMsg.priceVat</TranslatedString>
  </div>
);

const TrialEnduser = ({ archiveMonthly, archiveAnnually, additionalServices }) => (
  <div>
    <div className="flex justify-between">
      <div className="font-semibold">{t("prices.archive.title")}</div>
      <DocsLink docsKey="pricesArchive" />
    </div>
    <div className="mt-3 text-xs text-qb-text-a40">
      <TranslatedString>prices.archive.desc</TranslatedString>
    </div>

    <div className="mt-[18px] flex text-sm">
      <div className="flex-1">{t("prices.archive.monthly.label")}</div>
      <div className="w-40">€ {priceFormat(archiveMonthly)}</div>
    </div>

    <div className="flex mt-4 text-sm">
      <div className="flex-1">{t("prices.archive.annual.label")}</div>
      <div className="w-40">€ {priceFormat(archiveAnnually)}</div>
    </div>

    <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
      <div className="text-sm font-semibold">{t("prices.dailyMessages.title")}</div>
      <DocsLink docsKey="pricesDailyMessages" />
    </div>
    <div className="mt-3 mb-6 text-xs text-qb-text-a40">{t("prices.dailyMessages.desc")}</div>

    <div className="mb-2 text-sm font-semibold">{t("prices.dailyMessages.monthlyPrices")}</div>
    <DailyMessagesPriceTable prices={additionalServices["daily_extra_messages"]["customer"]["monthly"]} />

    <div className="mt-8 mb-2 text-sm font-semibold">{t("prices.dailyMessages.annuallyPrices")}</div>
    <DailyMessagesPriceTable prices={additionalServices["daily_extra_messages"]["customer"]["annually"]} />

    <div className="w-full mt-8">
      <DeliverySpotBanner hideCTA={true} />
    </div>

    <PriceVat />
  </div>
);

const TrialReseller = () => (
  <div>
    <div className="flex justify-between">
      <div className="text-sm font-semibold">{t("prices.webLabel.title")}</div>
      <DocsLink docsKey="pricesWebLabel" />
    </div>

    <div className="mt-3 text-xs text-qb-text-a40">
      <TranslatedString>prices.webLabel.desc</TranslatedString>
    </div>

    <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
      <div className="text-sm font-semibold">{t("prices.privateLabel.title")}</div>
      <DocsLink docsKey="pricesPrivateLabel" />
    </div>

    <div className="mt-3 text-xs text-qb-text-a40">{t("prices.privateLabel.desc")}</div>

    <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
      <div className="text-sm font-semibold">{t("prices.archive.title")}</div>
      <DocsLink docsKey="pricesArchive" />
    </div>

    <div className="mt-3 text-xs text-qb-text-a40">
      <TranslatedString>prices.trial.reseller.archive.desc</TranslatedString>
    </div>

    <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
      <div className="text-sm font-semibold">{t("prices.backup.title")}</div>
      <DocsLink docsKey="pricesBackup" />
    </div>

    <div className="mt-3 text-xs text-qb-text-a40">
      <TranslatedString>prices.trial.backup.desc</TranslatedString>
    </div>

    <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
      <div className="text-sm font-semibold">{t("prices.dailyMessages.title")}</div>
      <DocsLink docsKey="pricesDailyMessages" />
    </div>
    <div className="mt-3 mb-6 text-xs text-qb-text-a40">{t("prices.dailyMessages.desc")}</div>

    <div className="w-full mt-8">
      <DeliverySpotBanner hideCTA={true} />
    </div>
  </div>
);

const PaidEnduser = ({ archiveMonthly, archiveAnnually, additionalServices, isAnnual }) => (
  <div>
    <div className="flex justify-between">
      <div className="text-sm font-semibold">{t("prices.archive.title")}</div>
      <DocsLink docsKey="pricesArchive" />
    </div>

    <div className="mt-3 text-xs text-qb-text-a40">
      <TranslatedString>prices.paid.enduser.archive.desc</TranslatedString>
    </div>

    <div className="mt-[18px] flex text-sm">
      <div className="flex-1">
        {archiveMonthly ? t("prices.archive.monthly.label") : t("prices.archive.annual.label")}
      </div>
      <div className="w-40">€ {priceFormat(archiveMonthly ? archiveMonthly : archiveAnnually)}</div>
    </div>

    <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
      <div className="text-sm font-semibold">{t("prices.backup.title")}</div>
      <DocsLink docsKey="pricesBackup" />
    </div>

    <div className="mt-3 text-xs text-qb-text-a40">
      <TranslatedString>prices.paid.backup.desc</TranslatedString>
    </div>
    <div className="flex mt-4 text-sm">
      <div className="flex-1">{t("prices.backup.label")}</div>
      <div className="w-40">€ {priceFormat(additionalServices["mtm_restore"]["price"])} </div>
    </div>

    <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
      <div className="text-sm font-semibold">{t("prices.dailyMessages.title")}</div>
      <DocsLink docsKey="pricesDailyMessages" />
    </div>

    <div className="mt-3 mb-6 text-xs text-qb-text-a40">{t("prices.dailyMessages.desc")}</div>

    <div className="mb-2 text-sm font-semibold">
      {t(`prices.dailyMessages.${isAnnual ? "annually" : "monthly"}Prices`)}
    </div>
    <DailyMessagesPriceTable
      prices={additionalServices["daily_extra_messages"]["customer"][isAnnual ? "annually" : "monthly"]}
    />

    <div className="w-full mt-8">
      <DeliverySpotBanner />
    </div>

    <PriceVat />
  </div>
);

const PaidReseller = ({ archivePrice, additionalServices, isAdmin }) => {
  const { getLoggedUser } = useContext(AuthContext);
  const userCode = getLoggedUser().code;

  const [webLabelRequestStorage, setWebLabelRequestStorage] = useLocalStorage(`qb_web_label_request_${userCode}`, null);
  const [privateLabelRequestStorage, setPrivateLabelRequestStorage] = useLocalStorage(
    `qb_private_label_request_${userCode}`,
    null,
  );
  const [isPrivateLabelModalOpened, setPrivateLabelModalOpened] = useState(false);
  const [isWebLabelOpened, setWebLabelOpened] = useState(false);

  const history = useHistory();

  // unlock buttons after 1 hour
  for (let request of [
    { type: "private_label", value: privateLabelRequestStorage },
    { type: "web_label", value: webLabelRequestStorage },
  ]) {
    if (request.value?.date) {
      const requested = new Date(request.value.date);
      const now = new Date();

      const diffMilliseconds = now - requested;
      const diffHours = diffMilliseconds / 1000 / 60 / 60;

      if (diffHours >= 1) {
        if (request.type === "private_label") {
          setPrivateLabelRequestStorage(null);
        }

        if (request.type === "web_label") {
          setWebLabelRequestStorage(null);
        }
      }
    }
  }

  const onWebLabelRequest = async () => {
    setWebLabelRequestStorage({ status: "loading" });
    await new Promise((res) => setTimeout(() => res(), 1000));

    //eslint-disable-next-line
    const response = await api.requestAdditionalService("web_label");
    setWebLabelOpened(true);
    setWebLabelRequestStorage({ date: new Date(), status: "requested" });
  };

  const onPrivateLabelRequest = async () => {
    setPrivateLabelRequestStorage({ status: "loading" });

    //eslint-disable-next-line
    const response = await api.requestAdditionalService("private_label");

    setPrivateLabelModalOpened(true);
    setPrivateLabelRequestStorage({ date: new Date(), status: "requested" });
  };

  const handleLinkClick = (e) => {
    const targetLink = e.target.closest("a");
    if (!targetLink) return;
    e.preventDefault();

    const href = targetLink.href;
    if (!href) return;

    history.push({
      pathname: new URL(href).pathname,
    });
  };

  return (
    <div>
      <div className="flex justify-between">
        <div className="text-sm font-semibold">{t("prices.webLabel.title")}</div>
        <DocsLink docsKey="pricesWebLabel" />
      </div>

      <div className="mt-3 text-xs text-qb-text-a40">
        <TranslatedString>prices.webLabel.desc</TranslatedString>
      </div>

      <div className="flex items-center gap-8 mt-4">
        <div className="flex-auto">
          <div className="flex text-sm">
            <div className="flex-1">{t("prices.webLabel.firstModule")}</div>
            <div>€ {priceFormat(additionalServices["web_label"]["price"])}</div>
          </div>
          <div className="flex mt-4 text-sm">
            <div className="flex-1">{t("prices.webLabel.secondModule")}</div>
            <div>
              +€ {priceFormat(additionalServices["web_label"]["extra_module"])} / {t("prices.webLabelModule")}
            </div>
          </div>
        </div>

        <div className="flex justify-end shrink-0 basis-1/4">
          <Button
            disabled={Boolean(webLabelRequestStorage) || isAdmin}
            loading={webLabelRequestStorage && webLabelRequestStorage.status === "loading"}
            onClick={onWebLabelRequest}
          >
            {webLabelRequestStorage ? "prices.requested" : "prices.request"}
          </Button>
        </div>
      </div>

      <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
        <div className="text-sm font-semibold">{t("prices.privateLabel.title")}</div>
        <DocsLink docsKey="pricesPrivateLabel" />
      </div>

      <div className="mt-3 text-xs text-qb-text-a40">{t("prices.privateLabel.desc")}</div>

      <div className="flex items-center gap-8 mt-4">
        <div className="flex flex-auto text-sm">
          <div className="flex-1">{t("prices.privateLabel.label")}</div>
          <div>€ {priceFormat(additionalServices["private_label"]["price"])} </div>
        </div>

        <div className="flex justify-end shrink-0 basis-1/4">
          <Button
            disabled={Boolean(privateLabelRequestStorage) || isAdmin}
            loading={privateLabelRequestStorage && privateLabelRequestStorage.status === "loading"}
            onClick={onPrivateLabelRequest}
          >
            {privateLabelRequestStorage ? "prices.requested" : "prices.request"}
          </Button>
        </div>
      </div>

      <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
        <div className="text-sm font-semibold">{t("prices.archive.title")}</div>
        <DocsLink docsKey="pricesArchive" />
      </div>

      <div
        className="mt-3 text-xs text-qb-text-a40"
        onClick={handleLinkClick}
        dangerouslySetInnerHTML={{
          __html: t("prices.archive.desc"),
        }}
      ></div>

      <div className="flex items-center gap-8 mt-4">
        <div className="flex-1">
          <div className="flex text-sm">
            <div className="flex-1">{t("prices.archive.label")}</div>
            <div>€ {priceFormat(archivePrice)}</div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
        <div className="text-sm font-semibold">{t("prices.backup.title")}</div>
        <DocsLink docsKey="pricesBackup" />
      </div>

      <div className="mt-3 text-xs text-qb-text-a40">{t("prices.paid.backup.desc")}</div>

      <div className="flex items-center gap-8 mt-4">
        <div className="flex-1">
          <div className="flex text-sm">
            <div className="flex-1">{t("prices.backup.label")}</div>
            <div>€ {priceFormat(additionalServices["mtm_restore"]["price"])} </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4 mt-8 border-t border-t-qb-border-qb-border-gray">
        <div className="text-sm font-semibold">{t("prices.dailyMessages.title")}</div>
        <DocsLink docsKey="pricesDailyMessages" />
      </div>
      <div className="mt-3 mb-6 text-xs text-qb-text-a40">{t("prices.dailyMessages.desc")}</div>

      <div className="mb-2 text-sm font-semibold">{t("prices.dailyMessages.monthlyPrices")}</div>
      <DailyMessagesPriceTable prices={additionalServices["daily_extra_messages"]["reseller"]} />

      <div className="w-full mt-8">
        <DeliverySpotBanner />
      </div>

      <PriceVat />

      <Modal
        className=""
        opened={isPrivateLabelModalOpened || isWebLabelOpened}
        closeModal={() => {
          setPrivateLabelModalOpened(false);
          setWebLabelOpened(false);
        }}
        title={t("additionalservices.requested.title")}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: t("additionalservices.requested.desc", null, {
              service: isPrivateLabelModalOpened ? "Private Label" : "Web Label",
            }),
          }}
        ></div>
        <div className="">
          <Button
            onClick={() => {
              setPrivateLabelModalOpened(false);
              setWebLabelOpened(false);
            }}
            type="button"
            color="outline"
          >
            button.ok
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const getAdditionalServicesContent = (type, prices, isAdmin, isAnnual) => {
  switch (type) {
    case serviceType.TRIAL_ENDUSER:
      return (
        <TrialEnduser
          archiveMonthly={prices.archiveMonthly}
          archiveAnnually={prices.archiveAnnually}
          additionalServices={prices.additionalServices}
        />
      );

    case serviceType.TRIAL_RESELLER:
      return <TrialReseller />;

    case serviceType.PAID_ENDUSER:
      return (
        <PaidEnduser
          archiveMonthly={prices.archiveMonthly}
          archiveAnnually={prices.archiveAnnually}
          additionalServices={prices.additionalServices}
          isAdmin={isAdmin}
          isAnnual={isAnnual}
        />
      );

    case serviceType.PAID_RESELLER:
      return (
        <PaidReseller archivePrice={prices.archive} additionalServices={prices.additionalServices} isAdmin={isAdmin} />
      );
    default:
      return null;
  }
};

const AdditionalServices = ({ type, prices, className, isAdmin, isAnnual }) => (
  <div className={cx(className)}>{getAdditionalServicesContent(type, prices, isAdmin, isAnnual)}</div>
);

export default AdditionalServices;
